import ChevronDown2 from '@assets/icons/chevron-down-2';
import ChevronUp2 from '@assets/icons/chevron-up-2';
import DarkFaqFrequentlyQuestions from '@assets/icons/dark-faq-frequently-questions';
import DarkFaqNotFound from '@assets/icons/dark-faq-not-found';
import FaqFrequentlyQuestions from '@assets/icons/faq-frequently-questions';
import FaqNotFound from '@assets/icons/faq-not-found';
import Magnifier from '@assets/icons/magnifier';
import { Spacer } from '@components/helpers';
import { Grid } from '@devstart/react-bootstrap';
import { fetchPublicFAQs } from '@redux/actions';
import { ThemeInterface } from '@redux/prop-types';
import {
  publicCategoriesSelector,
  publicFAQsSelector,
  themeSelector
} from '@redux/selectors';
import Input from '@ui/Input';
import getFaqCategoryIcon from '@utils/faq-category-icons';
import { isDarkMode } from '@utils/theme';
import React, { KeyboardEvent, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import '../../pages/faq.css';

type FAQItem = {
  id: string;
  title: string;
  answer: string;
  category: {
    slug: string;
    name: string;
    description?: string;
  };
  orderFaq: number;
};

type CategoryItem = {
  id: string;
  name: string;
  slug: string;
  description?: string;
  order: number;
  active: boolean;
};

type UserFilters = {
  source?: string[] | string;
  group?: string | null;
};

type SearchFormData = {
  search: string;
};

type Props = {
  fetchPublicFAQs: typeof fetchPublicFAQs;
  faqs: FAQItem[];
  categories: CategoryItem[];
  theme: ThemeInterface;
  userFilters?: UserFilters;
};

const mapStateToProps = createSelector(
  publicFAQsSelector,
  publicCategoriesSelector,
  themeSelector,
  (faqs: FAQItem[], categories: CategoryItem[], theme: ThemeInterface) => ({
    faqs,
    categories,
    theme
  })
);

const mapDispatchToProps = {
  fetchPublicFAQs
};

function PublicFAQ({
  fetchPublicFAQs,
  faqs,
  categories,
  theme,
  userFilters
}: Props): JSX.Element {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>('');

  const { control, handleSubmit, watch, setValue, reset } =
    useForm<SearchFormData>({
      defaultValues: {
        search: ''
      }
    });

  const search = watch('search');

  useEffect(() => {
    if (userFilters) {
      fetchPublicFAQs(userFilters);
    } else {
      fetchPublicFAQs();
    }
  }, [fetchPublicFAQs, userFilters]);

  const toggleExpanded = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('search', event.target.value);
  };

  const onSubmit: SubmitHandler<SearchFormData> = data => {
    setActiveSearchTerm(data.search);
  };

  const categoriesMap = new Map<
    string,
    { name: string; description?: string }
  >();
  categories.forEach(category => {
    categoriesMap.set(category.slug, {
      name: category.name,
      description: category.description
    });
  });

  const categoriesWithFAQs = categories.filter(category =>
    faqs.some(faq => faq.category?.slug === category.slug)
  );

  const categoryFilteredFAQs =
    categoryFilter === 'all'
      ? faqs
      : faqs.filter(faq => faq.category?.slug === categoryFilter);

  const filteredFAQs = categoryFilteredFAQs.filter(faq => {
    if (!activeSearchTerm.trim()) return true;

    const searchLower = activeSearchTerm.toLowerCase();
    const titleMatch = faq.title.toLowerCase().includes(searchLower);
    const answerMatch = faq.answer.toLowerCase().includes(searchLower);

    return titleMatch || answerMatch;
  });

  return (
    <Grid className='faq-container'>
      <div className='faq-container-search'>
        <h1 className='faq-title'>Perguntas Frequentes</h1>
        <Controller
          name='search'
          control={control}
          render={({ field }) => (
            <Input
              style={{ width: '554px', maxWidth: '100%' }}
              placeholder='Digite uma palavra-chave'
              {...field}
              value={search}
              onChange={handleChangeSearch}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleSubmit(onSubmit)();
                }
              }}
              insideIcon={
                <Magnifier
                  style={{ cursor: 'pointer' }}
                  width={20}
                  height={20}
                  onClick={() => void handleSubmit(onSubmit)()}
                />
              }
            />
          )}
        />
      </div>

      <Spacer size='large' />
      <Spacer size='medium' />

      <div className='faq-content'>
        <aside className='faq-sidebar'>
          <h2 className='faq-sidebar-title'>Categorias</h2>
          {categoriesWithFAQs.length > 0 && (
            <nav className='faq-categories'>
              <button
                className={`faq-category-button ${
                  categoryFilter === 'all' ? 'active' : ''
                }`}
                onClick={() => {
                  setCategoryFilter('all');
                  reset();
                  setActiveSearchTerm('');
                }}
              >
                <div className='faq-category-content'>
                  <div className='faq-category-icon-wrapper'>
                    {isDarkMode(theme.currentTheme) ? (
                      <DarkFaqFrequentlyQuestions width={52} height={52} />
                    ) : (
                      <FaqFrequentlyQuestions width={52} height={52} />
                    )}
                  </div>
                  <div className='faq-category-text'>
                    <span className='faq-category-name'>
                      Todas as perguntas
                    </span>
                    <span className='faq-category-description'>
                      Ver todas as perguntas
                    </span>
                  </div>
                </div>
              </button>
              {categoriesWithFAQs.map(category => {
                const CategoryIcon = getFaqCategoryIcon(
                  category.slug,
                  isDarkMode(theme.currentTheme)
                );
                return (
                  <button
                    key={category.slug}
                    className={`faq-category-button ${
                      categoryFilter === category.slug ? 'active' : ''
                    }`}
                    onClick={() => {
                      setCategoryFilter(category.slug);
                      reset();
                      setActiveSearchTerm('');
                    }}
                  >
                    <div className='faq-category-content'>
                      <div className='faq-category-icon-wrapper'>
                        {CategoryIcon ? (
                          <CategoryIcon width={52} height={52} />
                        ) : isDarkMode(theme.currentTheme) ? (
                          <DarkFaqFrequentlyQuestions width={52} height={52} />
                        ) : (
                          <FaqFrequentlyQuestions width={52} height={52} />
                        )}
                      </div>
                      <div className='faq-category-text'>
                        <span className='faq-category-name'>
                          {category.name}
                        </span>
                        {category.description && (
                          <span className='faq-category-description'>
                            {category.description}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </nav>
          )}
        </aside>

        <main className='faq-main'>
          <h2 className='faq-main-title'>
            {categoryFilter === 'all'
              ? 'Todas as Perguntas'
              : categoriesMap.get(categoryFilter)?.name}
          </h2>
          {filteredFAQs.length === 0 ? (
            <div className='faq-empty'>
              {isDarkMode(theme.currentTheme) ? (
                <DarkFaqNotFound
                  style={{ alignSelf: 'center', padding: '10px' }}
                />
              ) : (
                <FaqNotFound style={{ alignSelf: 'center', padding: '10px' }} />
              )}
              <span style={{ paddingBottom: '8px' }}>Ops!</span>
              <p>
                Não encontramos resultados para sua busca.
                <br />
                Tente usar outras palavras ou explore as categorias ao lado.
              </p>
            </div>
          ) : (
            <div className='faq-list'>
              {filteredFAQs.map(faq => (
                <div key={faq.id} className='faq-item'>
                  <button
                    className='faq-question'
                    onClick={() => toggleExpanded(faq.id)}
                  >
                    <span>{faq.title}</span>
                    <span className='faq-icon'>
                      {expandedId === faq.id ? (
                        <ChevronUp2 width={16} height={16} />
                      ) : (
                        <ChevronDown2 width={16} height={16} />
                      )}
                    </span>
                  </button>
                  {expandedId === faq.id && (
                    <div
                      className='faq-answer'
                      dangerouslySetInnerHTML={{ __html: faq.answer }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </Grid>
  );
}

PublicFAQ.displayName = 'PublicFAQ';

export default connect(mapStateToProps, mapDispatchToProps)(PublicFAQ);
